import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getNanoVariantPetAgeMonths,
  getNanoVariantPetBreed,
  getNanoVariantPetGender,
  getNanoVariantPetName,
  getNanoVariantPetWeight,
} from '../../lib/util/petUrlParams';
import {
  setNanoReferralPetAgeCookie,
  setNanoReferralPetBreedCookie,
  setNanoReferralPetNameCookie,
  setNanoReferralPetGenderCookie,
  setNanoReferralPetWeightCookie,
} from '../../lib/util/nanoReferralPetNameCookie';
import styles from './NanoBanner.module.scss';
import { DateTime } from 'luxon';
import { useLocation } from 'react-router-dom';
import { StoreCart } from '../../types';
import {
  inCartMiniCollarCount,
  inCartPrepaidMembershipCount,
  inCartS3CollarCount,
} from '../ItemsInCart/CouponLineItemV2';
import { getNanoDeviceOfferEnabled } from '../../lib/nanoDeviceOffer';
import classNames from 'classnames';
import { PromotionBanner } from '@barkinglabs/promotions-react';
import { PromotionConfigurationKind } from '@barkinglabs/promotions-types';

// moved to lib/nanoDeviceOffer for the same reason as the param readers; re-exported to keep
// existing imports alive
export { getNanoDeviceOfferEnabled };

export function showNanoVariant() {
  if (!getNanoDeviceOfferEnabled()) {
    return false;
  }

  const petName = getNanoVariantPetName();
  const petAge = getNanoVariantPetAgeMonths();
  const petGender = getNanoVariantPetGender();
  const petWeight = getNanoVariantPetWeight();
  const petBreed = getNanoVariantPetBreed();

  if (!!petName && petAge >= 0 && !!petGender && !!petWeight && !!petBreed) {
    setNanoReferralPetNameCookie(petName);
    setNanoReferralPetAgeCookie(petAge.toString());
    setNanoReferralPetGenderCookie(petGender);
    setNanoReferralPetWeightCookie(petWeight);
    setNanoReferralPetBreedCookie(petBreed);
    return true;
  }

  return false;
}

// the pet url param readers moved to lib/util/petUrlParams so light consumers (and their tests)
// do not pull this component's animation dependencies; re-exported to keep existing imports alive
export {
  getNanoVariantPetAgeMonths,
  getNanoVariantPetBreed,
  getNanoVariantPetGender,
  getNanoVariantPetGenderSelect,
  getNanoVariantPetName,
  getNanoVariantPetWeight,
  hasNanoVariantPetName,
} from '../../lib/util/petUrlParams';

const CountdownTimer = () => {
  const { t } = useTranslation();
  const calculateTimeLeft = () => {
    const now = DateTime.local();
    let targetTime = now.set({ hour: 9, minute: 0, second: 0, millisecond: 0 });

    // If the current time is already past 9:00 AM today, set the target time to 9:00 AM tomorrow
    if (now >= targetTime) {
      targetTime = targetTime.plus({ days: 1 });
    }

    return targetTime.diff(now, ['hours', 'minutes', 'seconds']);
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const hours = Math.floor(timeLeft.hours).toString().padStart(2, '0');
  const minutes = Math.floor(timeLeft.minutes).toString().padStart(2, '0');
  const seconds = Math.floor(timeLeft.seconds).toString().padStart(2, '0');

  return (
    <div className={styles.countdown}>
      <div className={styles.expiresSoonDesktop}>
        <div>{t('expires')}</div>
        <div>{t('soon')}</div>
      </div>
      <div className={styles.expiresSoonMobile}>
        <div>{t('expires_soon')}</div>
      </div>
      <div className={styles.countdownClock}>
        <div className={styles.hours}>{hours}</div>
        {/* eslint-disable-next-line react/jsx-no-literals */}
        <div className={styles.timeSeparator}>:</div>
        <div className={styles.minutes}>{minutes}</div>
        {/* eslint-disable-next-line react/jsx-no-literals */}
        <div className={styles.timeSeparator}>:</div>
        <div className={styles.seconds}>{seconds}</div>
      </div>
    </div>
  );
};

const useAutoApplyNanoCouponOnCollarAdded = (cart: StoreCart, promoCodeApplied: boolean, onClick?: () => void) => {
  const cartItems = Object.values(cart.cartItems);

  const { pathname } = useLocation();

  useEffect(() => {
    const totalCollarCount = inCartS3CollarCount(cartItems) + inCartMiniCollarCount(cartItems);
    const monthToMonthCollarCount = inCartPrepaidMembershipCount(cartItems);
    // From the cart, we want to auto-apply the promo code by simulating a click event on the banner.
    if (
      !promoCodeApplied &&
      pathname.startsWith('/bag') &&
      totalCollarCount > 0 &&
      totalCollarCount - monthToMonthCollarCount > 0
    ) {
      onClick && onClick();
    }
    // eslint-disable-next-line
  }, [pathname]);
};

export function NanoBanner({
  cart,
  onClick,
  promoCodeApplied,
}: {
  cart: StoreCart;
  onClick?: () => void;
  promoCodeApplied: boolean;
}) {
  const { t } = useTranslation();
  useAutoApplyNanoCouponOnCollarAdded(cart, promoCodeApplied, onClick);

  const discountText = t('dollar_amount_off_all_pre_paid_plans_auto_applied_at_checkout', { discountAmount: 99 });

  return (
    <button className={classNames(styles.nanoBanner, 'nanoBanner')} onClick={onClick}>
      <div className={styles.textContainer}>
        {/* eslint-disable-next-line react/jsx-no-literals */}
        {!promoCodeApplied && <div className={styles.image}>🐶</div>}
        <div className={styles.titleContainer}>
          <div className={styles.title}>
            {promoCodeApplied ? t('offer_applied_to_cart') : t('exclusive_fi_nano_offer')}
          </div>
          {!promoCodeApplied && <div className={styles.subtitle}>{discountText}</div>}
        </div>
      </div>
      <CountdownTimer />
    </button>
  );
}

export function NanoBannerV2({
  cart,
  onClick,
  promoCodeApplied,
}: {
  cart: StoreCart;
  onClick?: () => void;
  promoCodeApplied: boolean;
}) {
  const { t } = useTranslation();
  useAutoApplyNanoCouponOnCollarAdded(cart, promoCodeApplied, onClick);

  return (
    <PromotionBanner
      className={'promotionBanner'}
      promotion={{
        configuration: {
          kind: PromotionConfigurationKind.SIMPLE,
          title: '',
          subtitle: t('dollar_amount_off_will_be_automatically_applied_in_your_cart', { amount: 99 }),
          couponCode: 'NANO99',
          countdown: 'static',
          extraCss: undefined,
          imageUrl: undefined,
          backgroundImageUrl: undefined,
        },
      }}
      promotionEnd={t('offer_ends_today')}
    />
  );
}
